import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import {List, ListItem} from '@material-ui/core';
import lodashOmit from 'lodash/omit';
import {useSelector, useDispatch} from 'react-redux';
import navigate from 'Data/actionCreators/kiosk/navigate';
import setJourneyStop from 'Data/actionCreators/journey/setJourneyStop';
import setSearchLocation from 'Data/actionCreators/journey/setSearchLocation';
import FeaturePropType from 'Utils/propTypes/FeaturePropType';
import {NO_RESULT_PLACEHOLDER} from 'Utils/labels';
import NoResultPlaceholder from 'Components/common/NoResultsPlaceholder/NoResultPlaceholder';
import AlphabetList from 'Components/common/AlphabetList/AlphabetList';
import {prependDigitalAssetsUrl} from 'Utils/prependDigitalAssetsUrl';
import * as analytics from 'Utils/analytics';
import useFilterSelection from 'Utils/useFilterSelection';
import TenantCard from './TenantCard';

const TenantsList = ({
    directoryContentSource,
    tenantFeatures,
    currentKioskFeature,
    isStandalone,
    contentSources,
    parentScrollerRef,
    setClearFilterHandler,
}) => {
    const dispatch = useDispatch();

    const {
        selectedFilters,
        isFilterSelected,
        isNoFilterSelected,
        selectFilter,
        toggleFilter,
        unselectAllFilters,
    } = useFilterSelection();

    if (setClearFilterHandler) {
        setClearFilterHandler(unselectAllFilters);
    }

    const [selectedContentSource, setSelectedContentSource] = useState(directoryContentSource);

    const displayAllContentInDirectory = useSelector(
        state => state.pageState.layout.config.settings.displayAllContentInParentVenueFilter
    );

    const isDirectoryContentSourceSelected =
        displayAllContentInDirectory &&
        directoryContentSource?.properties?.featureId ===
            selectedContentSource?.properties?.featureId;

    const menuColourDirectory = useSelector(
        state =>
            state.pageState.layout.config.signConfig.withInheritance.menuTab.menuColourDirectory
    );

    const primaryColors = useSelector(
        state => state.pageState.layout.config.settings.brandingConfig.primary.colors
    );

    /* tenantFeatures array is already sorted by tenant name */
    const tenantsList = [];
    const contentSourcesList = {};
    if (directoryContentSource?.properties?.featureId) {
        contentSourcesList[directoryContentSource.properties.featureId] = directoryContentSource;
    }

    const categoriesToFilter = {};

    tenantFeatures.forEach(tenantFeature => {
        if (!tenantFeature.properties.tenant.isShowInDirectory) {
            return;
        }

        const tenantCategory = tenantFeature.properties.tenant.category;
        if (
            selectedContentSource &&
            tenantCategory?.name &&
            !categoriesToFilter[tenantCategory.name]
        ) {
            if (
                isDirectoryContentSourceSelected ||
                tenantFeature.properties.contentSources.find(
                    f => f.properties.featureId === selectedContentSource?.properties?.featureId
                )
            ) {
                categoriesToFilter[tenantCategory.name] = tenantCategory;
            }
        }

        if (
            !isNoFilterSelected() &&
            (!tenantCategory?.name || !isFilterSelected(tenantCategory.name))
        ) {
            return;
        }

        if (
            !isDirectoryContentSourceSelected &&
            selectedContentSource &&
            !tenantFeature.properties.contentSources.find(
                cs => cs.properties.featureId === selectedContentSource.properties.featureId
            )
        ) {
            return;
        }

        tenantsList.push(tenantFeature);
    });

    if (isStandalone) {
        contentSources.forEach(contentSourceFeature => {
            if (
                tenantFeatures.find(feature =>
                    feature.properties.contentSources.find(
                        f => f.properties.featureId === contentSourceFeature.properties.featureId
                    )
                )
            ) {
                contentSourcesList[
                    contentSourceFeature.properties.featureId
                ] = contentSourceFeature;
            }
        });
    }

    useEffect(
        () => {
            setSelectedContentSource(directoryContentSource);
        },
        [directoryContentSource]
    );

    useEffect(
        () => {
            const categories = Object.values(categoriesToFilter);
            if (categories.length > 1) {
                unselectAllFilters();
            } else if (categories[0]) {
                selectFilter(categories[0]?.name);
            }
        },
        [selectedContentSource]
    );

    const categoriesList = Object.values(categoriesToFilter).map(category => {
        return (
            <div
                key={category.id}
                className={classNames('pill', {
                    active: isFilterSelected(category.name),
                })}
                style={
                    isFilterSelected(category.name)
                        ? {
                              color: primaryColors.text,
                              backgroundColor: primaryColors.background,
                          }
                        : {}
                }
                onClick={() => {
                    analytics.sendDirectoryEvent(
                        analytics.ACTIONS.TENANT,
                        isFilterSelected(category.name) ? 'Unselect Category' : 'Select Category',
                        {
                            CategoryName: category.name,
                            CategoryId: category.id,
                        }
                    );
                    toggleFilter(category.name);
                }}
            >
                {category.name}
            </div>
        );
    });

    const renderList = () => (
        <>
            {tenantsList.length ? (
                <AlphabetList
                    className={classNames('directory-list-wrapper', {
                        'full-height': Object.keys(contentSourcesList).length === 1 && isStandalone,
                        embedded: !isStandalone,
                    })}
                    parentScrollerRef={parentScrollerRef}
                    data={tenantsList}
                    indexingPath="properties.displayText"
                    generateFn={tenantFeature => {
                        return (
                            <TenantCard
                                key={tenantFeature.id}
                                tenantFeature={tenantFeature}
                                selectTenantFeature={tenant => {
                                    const analyticsExtraFields = {
                                        TenantId: tenant.properties?.tenant?.uuid,
                                        TenantName: tenant.properties?.tenant?.name,
                                        CategoryNames: selectedFilters ?? 'All',
                                        CategoryIds: Object.values(categoriesToFilter)
                                            .filter(cat => isFilterSelected(cat.name))
                                            .map(cat => cat.id),
                                    };
                                    analytics.sendDirectoryEvent(
                                        analytics.ACTIONS.TENANT,
                                        'Select',
                                        analyticsExtraFields
                                    );
                                    dispatch(navigate('featureDetailsCard', {item: tenant}));
                                    const cleanFeature = lodashOmit(tenantFeature, 'icon');
                                    dispatch(
                                        setJourneyStop({
                                            index: 1,
                                            feature: cleanFeature,
                                            zoomToResult: true,
                                        })
                                    );
                                    dispatch(
                                        setSearchLocation({
                                            feature: tenant,
                                            zoomToResult: true,
                                        })
                                    );
                                }}
                                currentKioskFeature={currentKioskFeature}
                            />
                        );
                    }}
                />
            ) : (
                <div
                    className={classNames('explorer-card-no-results', {
                        'full-height': Object.keys(contentSourcesList).length === 1,
                    })}
                >
                    <NoResultPlaceholder
                        {...NO_RESULT_PLACEHOLDER.tenants}
                        backgroundColor={menuColourDirectory}
                    />
                </div>
            )}
        </>
    );

    if (!isStandalone) {
        return renderList();
    }

    return (
        <div className="content-container tenants-list">
            <div className="content-stack">
                <div className="content-header">
                    {selectedContentSource?.properties && (
                        <div>
                            <p className="trim-text" data-testid="content-source-name">
                                {selectedContentSource.properties.displayText ||
                                    selectedContentSource.properties.featureId}
                            </p>
                        </div>
                    )}
                    <div className="list-filters">{categoriesList}</div>
                </div>
                <div className="content-body content-grow-tenants p-0">{renderList()}</div>
                {Object.keys(contentSourcesList).length > 1 && (
                    <div className="content-sources" data-testid="content-sources-list">
                        <h6>EXPLORE</h6>
                        <List className="content-sources-list">
                            {Object.values(contentSourcesList).map(contentSource => (
                                <ListItem
                                    className="list-item trim-text"
                                    key={contentSource.id}
                                    style={{
                                        ...(contentSource?.properties?.imageUrls?.thumb
                                            ? {
                                                  backgroundImage: `linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2)),
                                   url('${prependDigitalAssetsUrl(
                                       contentSource?.properties?.imageUrls?.thumb
                                   )}')`,
                                              }
                                            : {
                                                  backgroundImage: `linear-gradient(0deg, rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0.2))`,
                                              }),
                                        backgroundPosition: '50% 50%',
                                        opacity:
                                            contentSource?.properties?.featureId ===
                                            selectedContentSource?.properties?.featureId
                                                ? 1
                                                : 0.5,
                                    }}
                                    onClick={() => {
                                        setSelectedContentSource(contentSource);
                                    }}
                                >
                                    <span>
                                        {contentSource.properties.displayText ||
                                            contentSource.properties.featureId}
                                    </span>
                                </ListItem>
                            ))}
                        </List>
                    </div>
                )}
            </div>
        </div>
    );
};

TenantsList.defaultProps = {
    isStandalone: true,
};

TenantsList.propTypes = {
    isStandalone: PropTypes.bool,
    contentSources: PropTypes.array,
    directoryContentSource: PropTypes.object,
    tenantFeatures: PropTypes.array.isRequired,
    currentKioskFeature: FeaturePropType,
    parentScrollerRef: PropTypes.object,
    setClearFilterHandler: PropTypes.func,
};

export default TenantsList;
